import { Flex, GmgTheme, IconArrowDropDown, IconWarning, ImageButton, InputTypeTextReduced, Spinner, Tooltip } from '@gmg/gmg-react-components';
import { FunctionComponent } from 'react';
import { useTranslation } from 'react-i18next';
import { LicenseSystem } from 'src/viewModels/LicenseSystem';
import styled, { useTheme } from 'styled-components';
import IconKeyFilled from '../../assets/svg/icon_key_filled.svg';
import IconKeyOutlined from '../../assets/svg/icon_key_outlined.svg';
import LicenseManagementAction, { TargetLicenseSystemData } from './LicenseManagementAction';

export interface LicenseSystemRowProps {
    attributes: Omit<LicenseSystem, 'licenses' | 'lastSeenDate'> & {};
    isLicensesAccordionEmpty: boolean;
    isInStatusWarning: boolean;
    isEditingPending: boolean;
    isExpanded: boolean;
    isRequestTemporaryLicensesButtonShown: boolean;
    isAllowedToUseLicenseSystemsOffline: boolean;
    targetLicenseSystems: Array<TargetLicenseSystemData>;
    onArrowClick: (id: string) => void;
    setLocation: (location: string) => void;
    onChangeLocation: () => void;
};

const LicenseSystemRow: FunctionComponent<LicenseSystemRowProps> = (props) => {
    const theme: GmgTheme = useTheme();
    const { t } = useTranslation();
    const keyIcon = props.isLicensesAccordionEmpty ? <IconKeyOutlined /> : <IconKeyFilled />;

    const cpGoStatus = props.attributes.cpGo.isOnline && props.attributes.cpGo.isLicensed
        ? t('LicenseSystemRow.Statuses.available_title', 'Available')
        : '-';

    return (
        <Row
            data-testid="row"
            aria-label={`license system item for: ${props.attributes.id}`}
            isExpanded={props.isExpanded}
        >
            <Flex gap="16px">
                <StyledButton
                    aria-label={`expand license options for system: ${props.attributes.id}`}
                    isExpanded={props.isExpanded}
                    icon={<IconArrowDropDown />}
                    isExpandable={!props.isLicensesAccordionEmpty}
                    onClick={() => {
                        if (props.isLicensesAccordionEmpty) return;

                        props.onArrowClick(props.attributes.id);
                    }}
                />
            </Flex>
            <Flex gap="8px" alignItems="center" className="px12_400_normal" aria-label="license-system-id" role="cell">

                <IconWrapper>{keyIcon}</IconWrapper>
                {props.attributes.id}
                {props.isInStatusWarning && <Tooltip message={t('LicenseManagementTable.license_validity.warning_tooltip')}><IconWrapper><IconWarning color={theme.colors.warning} aria-label="warning sign" /></IconWrapper></Tooltip>}
            </Flex>

            <CenteredFlexCell className="px12_400_normal" aria-label="license-system-type" role="cell">
                {
                    /* Calling the t function with an array of keys enables you to translate dynamic keys providing a non specific fallback value. */
                    /* The second item in the array is the fallback in case the key does not exist. */
                }
                {t([`LicenseManagementTable.license_type_items.${props.attributes.licenseType}`,
                    'LicenseManagementTable.license_type_items.undefined'], `${props.attributes.licenseType}`)}
            </CenteredFlexCell>

            <CenteredFlexCell className="px12_400_normal" aria-label="license-system-computer-name" role="cell">
                {props.attributes.computerName}
                {props.attributes.hasPendingProcess && (
                    <span aria-label='pending process icon'>
                        <Spinner />
                    </span>
                )}
            </CenteredFlexCell>

            <CenteredFlexCell className="px12_400_normal" aria-label="license-system-location" role="cell">
                <InputTypeTextReduced
                    aria-label="license-system-location"
                    role="textbox"
                    readOnly={props.isEditingPending}
                    value={props.attributes.location}
                    onChange={(e) => { props.setLocation(e.target.value); }}
                    onBlur={() => { props.onChangeLocation(); }}
                    width="180px"
                />
            </CenteredFlexCell>

            <CenteredFlexCell
                role="cell"
                className="px12_400_normal"
                aria-label={`license-system-suc for ${props.attributes.id}`}
            >
                {props.attributes.hasActiveSuc
                    ? t('LicenseSystemRow.Statuses.active_title', 'Active')
                    : t('LicenseSystemRow.Statuses.inactive_title', 'Inactive')
                }
            </CenteredFlexCell>

            <CenteredFlexCell className="px12_400_normal" aria-label={`cpGo info for license-system ${props.attributes.id}`} role="cell">
                {cpGoStatus}
            </CenteredFlexCell>

            <CenteredFlexCellLast>
                <LicenseManagementAction
                    licenseSystemId={props.attributes.id}
                    isRequestTemporaryLicensesButtonShown={props.isRequestTemporaryLicensesButtonShown}
                    isTransferButtonShown={props.attributes.licenseType === 'online'}
                    isTransferSupported={props.attributes.isTransferSupported}
                    targetLicenseSystems={props.targetLicenseSystems}
                    isLicenseSystemUpdateAvailable={props.attributes.isLicenseSystemUpdateAvailable}
                    isAllowedToUseLicenseSystemsOffline={props.isAllowedToUseLicenseSystemsOffline}
                />
            </CenteredFlexCellLast>

        </Row>
    );
};

const Row = styled.div<{ isExpanded: boolean }>`
    display: grid;
    /* First and second column should be aligned with the licenses accordion */
    grid-template-columns: 8px minmax(100px, 3fr) repeat(3, minmax(100px, 2fr)) repeat(1, minmax(50px, 1fr)) repeat(2, minmax(100px, 1fr));
    /* grid-template-columns: 8px minmax(200px, 3fr) repeat(3, minmax(200px, 2fr)) 100px repeat(1, minmax(200px, 1fr)) 200px; */
    column-gap: 20px;
    min-width: min-content;
    height: 60px;
    min-height: 60px;
    padding-left: 20px;
    background-color: ${props => props.isExpanded ? props.theme.colors.highlightLight : props.theme.colors.backgroundPaper};
    border-bottom: 1px solid ${props => props.isExpanded ? props.theme.colors.highlight : 'transparent'};
    border-top: 1px solid ${props => props.isExpanded ? props.theme.colors.highlight : 'transparent'};
    z-index: 0;
    .invisibleSVG svg {
        display: none;
    }

    &:hover {
        border-color: transparent;
        background-color: ${props => props.theme.colors.highlightLight};
        .invisibleSVG svg {
            display: block;
        }
    }

    @media (max-width: 1250px) {
  transform: translateX(-20px)
}
`;

const CenteredFlexCell = styled.div`
    display: flex;
    align-items: center;
    gap: 8px;
`;

const CenteredFlexCellLast = styled(CenteredFlexCell)`
  position: sticky;
  right: 0;
  z-index: 10;
`;

const StyledButton = styled(ImageButton) <{ isExpanded: boolean; isExpandable: boolean }>`
    cursor: ${props => props.isExpandable ? 'pointer' : 'default'};
    opacity: ${props => props.isExpandable ? 1 : 0};;
    transform: ${props => props.isExpanded ? 'rotate(0.5turn)' : 'rotate(0)'};
    transition: 0.3s ease transform;
    height: 100%;
`;

const IconWrapper = styled.span`
    color: ${props => props.theme.colors.textWeak};
`;

export default LicenseSystemRow;
